// Filter bar above the Activity list. Reads/writes URL params via
// ``useActivityFilters``; this component is dumb — every interaction
// just patches the filter set.

import type { ActivityUiState } from "@/hooks/useActivityFilters";
import { cn } from "@/utils/cn";

const ROUTE_CLASSES = ["lookup", "analysis", "deep_work"] as const;

// Keep this list in sync with backend SUB_AGENT_LABELS / the sub-agent
// registry. Order matches the visual sidebar grouping.
const SUB_AGENTS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "regulatory", label: "Regulatory" },
  { id: "vulnerability", label: "Vulnerability" },
  { id: "legal_review", label: "Legal Review" },
  { id: "vendor_risk", label: "Vendor Risk" },
  { id: "framework_crosswalk", label: "Framework Crosswalk" },
  { id: "incident_response", label: "Incident Response" },
  { id: "trust_eidas", label: "Trust & eIDAS" },
  { id: "aml_fraud", label: "AML & Fraud" },
  { id: "pen_test", label: "Pen Test" },
  { id: "detection_engineering", label: "Detection Engineering" },
  { id: "cloud_security", label: "Cloud Security" },
  { id: "ai_security", label: "AI Security" },
  { id: "identity_crypto", label: "Identity & Crypto" },
  { id: "data_governance", label: "Data Governance" },
];

interface ChipProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function Chip({ active, onClick, children }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-sm border px-2 py-0.5 font-mono text-[0.7rem] uppercase tracking-wider transition-colors",
        active
          ? "border-oxblood-500 bg-oxblood-500 text-parchment-50"
          : "border-ruleline bg-parchment-50 text-warmgray-500 hover:border-oxblood-500 hover:text-oxblood-500",
      )}
    >
      {children}
    </button>
  );
}

interface NumInputProps {
  value: number | null | undefined;
  onCommit: (next: number | null) => void;
  placeholder: string;
  step?: string;
  min?: string;
  max?: string;
}

function NumInput({ value, onCommit, placeholder, step, min, max }: NumInputProps) {
  return (
    <input
      type="number"
      defaultValue={value ?? ""}
      key={String(value ?? "")}
      onBlur={(e) => {
        const raw = e.target.value.trim();
        if (raw === "") return onCommit(null);
        const n = Number(raw);
        onCommit(Number.isFinite(n) ? n : null);
      }}
      placeholder={placeholder}
      step={step}
      min={min}
      max={max}
      className="w-20 rounded border border-ruleline bg-parchment-50 px-1.5 py-0.5 font-mono text-[0.7rem] text-espresso-700 focus:border-oxblood-500 focus:outline-none"
    />
  );
}

interface TextInputProps {
  value: string | null | undefined;
  onCommit: (next: string | null) => void;
  placeholder: string;
  width?: string;
}

function TextInput({ value, onCommit, placeholder, width = "w-24" }: TextInputProps) {
  return (
    <input
      type="text"
      defaultValue={value ?? ""}
      key={String(value ?? "")}
      onBlur={(e) => {
        const raw = e.target.value.trim();
        onCommit(raw === "" ? null : raw);
      }}
      placeholder={placeholder}
      className={cn(
        "rounded border border-ruleline bg-parchment-50 px-1.5 py-0.5 font-mono text-[0.7rem] text-espresso-700 focus:border-oxblood-500 focus:outline-none",
        width,
      )}
    />
  );
}

export function ActivityFilters({ state }: { state: ActivityUiState }) {
  const { filters, patchFilters, clearFilters, toggleRouteClass, toggleSubAgent } = state;
  const activeRoutes = new Set(filters.routeClasses ?? []);
  const activeAgents = new Set(filters.subAgents ?? []);

  const hasAnyFilter =
    activeRoutes.size > 0 ||
    activeAgents.size > 0 ||
    filters.since != null ||
    filters.until != null ||
    filters.minGrounding != null ||
    filters.maxGrounding != null ||
    filters.refineOnly === true ||
    filters.minCost != null ||
    filters.maxCost != null ||
    filters.minLatencyMs != null ||
    filters.maxLatencyMs != null ||
    filters.errorOnly === true;

  return (
    <section className="mb-6 rounded border border-ruleline bg-parchment-100 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="smallcaps text-oxblood-500">Filters</p>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearFilters}
            className="font-mono text-[0.7rem] uppercase tracking-wider text-warmgray-500 hover:text-oxblood-500"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 font-mono text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            Route
          </span>
          {ROUTE_CLASSES.map((rc) => (
            <Chip key={rc} active={activeRoutes.has(rc)} onClick={() => toggleRouteClass(rc)}>
              {rc.replace("_", " ")}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 font-mono text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            Sub-agent
          </span>
          {SUB_AGENTS.map((sa) => (
            <Chip key={sa.id} active={activeAgents.has(sa.id)} onClick={() => toggleSubAgent(sa.id)}>
              {sa.label}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[0.65rem] uppercase tracking-wider text-warmgray-400">
              Date
            </span>
            <input
              type="date"
              defaultValue={(filters.since ?? "").slice(0, 10)}
              key={`since-${filters.since ?? ""}`}
              onBlur={(e) => {
                const raw = e.target.value;
                patchFilters({ since: raw ? `${raw}T00:00:00+00:00` : null });
              }}
              className="rounded border border-ruleline bg-parchment-50 px-1.5 py-0.5 font-mono text-[0.7rem] text-espresso-700 focus:border-oxblood-500 focus:outline-none"
            />
            <span className="font-mono text-[0.7rem] text-warmgray-400">→</span>
            <input
              type="date"
              defaultValue={(filters.until ?? "").slice(0, 10)}
              key={`until-${filters.until ?? ""}`}
              onBlur={(e) => {
                const raw = e.target.value;
                patchFilters({ until: raw ? `${raw}T00:00:00+00:00` : null });
              }}
              className="rounded border border-ruleline bg-parchment-50 px-1.5 py-0.5 font-mono text-[0.7rem] text-espresso-700 focus:border-oxblood-500 focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[0.65rem] uppercase tracking-wider text-warmgray-400">
              Grounding
            </span>
            <NumInput
              value={filters.minGrounding}
              onCommit={(v) => patchFilters({ minGrounding: v })}
              placeholder="0.0"
              step="0.05"
              min="0"
              max="1"
            />
            <span className="font-mono text-[0.7rem] text-warmgray-400">→</span>
            <NumInput
              value={filters.maxGrounding}
              onCommit={(v) => patchFilters({ maxGrounding: v })}
              placeholder="1.0"
              step="0.05"
              min="0"
              max="1"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[0.65rem] uppercase tracking-wider text-warmgray-400">
              Cost $
            </span>
            <TextInput
              value={filters.minCost}
              onCommit={(v) => patchFilters({ minCost: v })}
              placeholder="0.00"
              width="w-16"
            />
            <span className="font-mono text-[0.7rem] text-warmgray-400">→</span>
            <TextInput
              value={filters.maxCost}
              onCommit={(v) => patchFilters({ maxCost: v })}
              placeholder="∞"
              width="w-16"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[0.65rem] uppercase tracking-wider text-warmgray-400">
              Latency ms
            </span>
            <NumInput
              value={filters.minLatencyMs}
              onCommit={(v) => patchFilters({ minLatencyMs: v == null ? null : Math.round(v) })}
              placeholder="0"
              step="100"
              min="0"
            />
            <span className="font-mono text-[0.7rem] text-warmgray-400">→</span>
            <NumInput
              value={filters.maxLatencyMs}
              onCommit={(v) => patchFilters({ maxLatencyMs: v == null ? null : Math.round(v) })}
              placeholder="∞"
              step="100"
              min="0"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={filters.refineOnly === true}
              onChange={(e) => patchFilters({ refineOnly: e.target.checked || undefined })}
              className="h-3 w-3 accent-oxblood-500"
            />
            <span className="font-mono text-[0.7rem] uppercase tracking-wider text-warmgray-500">
              refine only
            </span>
          </label>

          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={filters.errorOnly === true}
              onChange={(e) => patchFilters({ errorOnly: e.target.checked || undefined })}
              className="h-3 w-3 accent-oxblood-500"
            />
            <span className="font-mono text-[0.7rem] uppercase tracking-wider text-warmgray-500">
              errors only
            </span>
          </label>
        </div>
      </div>
    </section>
  );
}
